import { useToast } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { BankAccountWithInstitutionAndDesignationGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { useGetCreditFacilitySummariesQuery } from 'borrower/domains/fundingRequests/requests/fundingRequests.gql.generated';
import { FundingRequestLandingCurrentRequest } from 'borrower/domains/fundingRequests/ui/landing/CurrentRequest';
import { FundingRequestLandingDefault } from 'borrower/domains/fundingRequests/ui/landing/Default';
import { LoadingView } from 'borrower/domains/fundingRequests/ui/shared/Loading';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import CreditFacilityMissing from 'packages/credit-facility/ui/CreditFacilityMissing';

/**
 * This container component is responsible for fetching CreditFacilitySummaries from backend and
 * rendering an appropriate components based on the response data.
 *
 * Default view , can initiate new FundingRequest
 * CurrentRequest view, will show request summmary and provides link to the page wih further CTA.
 */
export const FundingRequestLandingViewContainer = () => {
  const toast = useToast();
  const { creditFacilityId } = useCreditFacilityContext();
  const {
    data: getCreditFacilityQueryResponseData,
  } = useGetCreditFacilitySummariesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
  });

  const currentCreditFacilitySummary = useMemo(() => {
    if (getCreditFacilityQueryResponseData) {
      const { creditFacilitySummaries } = getCreditFacilityQueryResponseData;
      return _.find(
        creditFacilitySummaries,
        (creditFacilitySummary) =>
          creditFacilitySummary.id === creditFacilityId,
      );
    }
    return undefined;
  }, [getCreditFacilityQueryResponseData, creditFacilityId]);

  // We need Credit Facility Summaries data to continue
  if (!getCreditFacilityQueryResponseData) {
    return <LoadingView />;
  }

  if (!currentCreditFacilitySummary) {
    return <CreditFacilityMissing />;
  }

  const currentFundingRequest =
    currentCreditFacilitySummary?.currentFundingRequest;

  if (currentFundingRequest) {
    // transform bankAccounts data to `id -> bankAccount` map
    const { bankAccounts } = currentFundingRequest;
    const bankAccountMap = new Map(
      bankAccounts.map((v): [
        string,
        BankAccountWithInstitutionAndDesignationGQLType,
      ] => [v.id, v as BankAccountWithInstitutionAndDesignationGQLType]),
    );
    return (
      <FundingRequestLandingCurrentRequest
        bankAccountMap={bankAccountMap}
        currentFundingRequest={currentFundingRequest}
      />
    );
  }

  return <FundingRequestLandingDefault creditFacilityId={creditFacilityId} />;
};
